import { useEffect, useState } from 'react'
import { api } from '../../api'
import './GcsExportDialog.css'

/**
 * GCS Import 모달 다이얼로그 (KBNL-99).
 *
 * GcsExportDialog 의 폴더 탐색 UI 를 재사용하되, 파일을 클릭하면 그 내용을
 * 읽어서 onImported(content, path) 콜백으로 넘긴다 (편집 화면에 로드).
 *
 * Props:
 *   open         — boolean
 *   onClose      — 닫기 콜백
 *   defaultPath  — 시작 GCS 경로 (예: 'gs://kb-agent-tmp/')
 *   onImported   — (content: string, path: string) => void
 *   showToast    — 토스트 함수
 *   title        — 모달 제목
 */
export default function GcsImportDialog({
  open,
  onClose,
  defaultPath = '',
  onImported,
  showToast,
  title = 'GCS 에서 Import',
}) {
  const [currentPath, setCurrentPath] = useState(defaultPath || '')
  const [pathInput, setPathInput] = useState(defaultPath || '')
  const [listing, setListing] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [importing, setImporting] = useState(false)

  useEffect(() => {
    if (!open) return
    setCurrentPath(defaultPath || '')
    setPathInput(defaultPath || '')
    setError('')
  }, [open, defaultPath])

  useEffect(() => {
    if (!open) return
    if (!currentPath) { setListing(null); return }
    let cancelled = false
    setLoading(true); setError('')
    api.gcsList(currentPath)
      .then(res => { if (!cancelled) setListing(res) })
      .catch(e => { if (!cancelled) { setError(e.message || String(e)); setListing(null) } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, currentPath])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const handleEnterFolder = (folder) => {
    setCurrentPath(folder.path)
    setPathInput(folder.path)
  }
  const handleGoUp = () => {
    if (listing?.parent_path) {
      setCurrentPath(listing.parent_path)
      setPathInput(listing.parent_path)
    }
  }
  const handlePathCommit = () => {
    const p = (pathInput || '').trim()
    if (!p) return
    setCurrentPath(p.endsWith('/') ? p : p + '/')
  }

  const handlePickFile = async (file) => {
    if (importing) return
    setImporting(true)
    try {
      const res = await api.gcsImport(file.path)
      onImported?.(res.content || '', res.path || file.path)
      showToast?.('success', `Import 완료 — ${res.path || file.path}`)
      onClose?.()
    } catch (e) {
      showToast?.('error', `Import 실패: ${e.message}`)
    } finally {
      setImporting(false)
    }
  }

  if (!open) return null

  return (
    <div className="gcs-modal-backdrop" onClick={onClose}>
      <div className="gcs-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="gcs-modal-head">
          <span className="gcs-modal-title">{title}</span>
          <button className="gcs-close-btn" onClick={onClose} title="닫기">✕</button>
        </div>

        <div className="gcs-path-bar">
          <button className="btn-xs" onClick={handleGoUp} disabled={!listing?.parent_path || loading} title="상위 폴더로">↑ 상위</button>
          <input
            type="text"
            className="gcs-path-input"
            value={pathInput}
            onChange={e => setPathInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handlePathCommit() }}
            placeholder="gs://bucket/prefix/"
          />
          <button className="btn-xs" onClick={handlePathCommit} disabled={loading}>이동</button>
        </div>

        <div className="gcs-list">
          {loading && <div className="gcs-loading">불러오는 중...</div>}
          {!loading && error && <div className="gcs-error">⚠ {error}</div>}
          {!loading && !error && listing && (
            <>
              {listing.folders.length === 0 && listing.files.length === 0 && (
                <div className="gcs-empty">(비어 있음)</div>
              )}
              {listing.folders.map(f => (
                <div
                  key={f.path}
                  className="gcs-row gcs-row-folder"
                  onDoubleClick={() => handleEnterFolder(f)}
                  onClick={() => handleEnterFolder(f)}
                  title="폴더로 이동"
                >
                  <span className="gcs-row-icon">📁</span>
                  <span className="gcs-row-name">{f.name}/</span>
                </div>
              ))}
              {listing.files.map(f => (
                <div
                  key={f.path}
                  className="gcs-row gcs-row-file"
                  onClick={() => handlePickFile(f)}
                  title="이 파일을 편집 화면에 불러오기"
                >
                  <span className="gcs-row-icon">📄</span>
                  <span className="gcs-row-name">{f.name}</span>
                  <span className="gcs-row-meta">{(f.size || 0).toLocaleString()} B</span>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="gcs-footer">
          <div className="gcs-target-preview">
            {importing ? '불러오는 중...' : '파일을 클릭하면 현재 입력란에 불러옵니다.'}
          </div>
          <div className="gcs-actions">
            <button className="btn-secondary" onClick={onClose} disabled={importing}>닫기</button>
          </div>
        </div>
      </div>
    </div>
  )
}
